import type { Item } from '@hugo/core'
import { TriangleAlertIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select'
import { formatMinutes } from '@/lib/format'
import { useRunTool, useSettings } from '@/lib/items'
import { defaultEstimateLabel, ESTIMATE_CHOICES, planSentence, userToday } from '@/lib/plan'
import { useUserNow } from '@/lib/session'
import { attention } from '@/lib/styles'
import { cn } from '@/lib/utils'

/** The time an open task or promise needs: hugo's default by type until the user picks one. */
export function EstimateSelect({ item, id }: { item: Item; id: string }) {
  const { run } = useRunTool()
  const settings = useSettings()
  const saved = item.estimateMinutes === null ? '' : String(item.estimateMinutes)
  /** The choice right away; the server's once it answered (or again if the change failed). */
  const [value, setValue] = useState(saved)
  useEffect(() => setValue(saved), [saved])
  const { estimateMinutes } = item
  const choices =
    estimateMinutes === null || ESTIMATE_CHOICES.includes(estimateMinutes)
      ? ESTIMATE_CHOICES
      : [...ESTIMATE_CHOICES, estimateMinutes].sort((a, b) => a - b)

  const change = async (next: string) => {
    setValue(next)
    const minutes = next ? Number(next) : null
    const result = await run(
      'item.update',
      { id: item.id, estimateMinutes: minutes },
      { done: minutes ? `Time needed: ${formatMinutes(minutes)}` : "Time needed: hugo's default" },
    )
    if (!result) setValue(saved)
  }

  return (
    <NativeSelect
      id={id}
      value={value}
      size="sm"
      aria-describedby="item-plan-text"
      className="w-full"
      onChange={(event) => void change(event.target.value)}
    >
      <NativeSelectOption value="">
        {defaultEstimateLabel(item, settings.data?.rules.estimateMinutes)}
      </NativeSelectOption>
      {choices.map((minutes) => (
        <NativeSelectOption key={minutes} value={minutes}>
          {formatMinutes(minutes)}
        </NativeSelectOption>
      ))}
    </NativeSelect>
  )
}

/**
 * When hugo's plan gets an open task or promise done, at the hours a day set in Settings, in one
 * to three sentences; a hint when hugo does not plan it yet.
 */
export function PlanText({
  item,
  timeZone,
  personal = false,
}: {
  item: Item
  timeZone: string
  personal?: boolean
}) {
  const settings = useSettings()
  const now = useUserNow(60_000)
  const { year } = userToday(now, timeZone)
  const { plan } = item
  if (personal) {
    return (
      <p id="item-plan-text" className="text-xs text-muted-foreground" data-testid="plan-text">
        Personal: hugo keeps it out of your working hours, so it never pushes your work.
      </p>
    )
  }
  if (!plan || (item.kind !== 'task' && item.kind !== 'owe')) {
    return (
      <p id="item-plan-text" className="text-xs text-muted-foreground">
        Give it a date, or put it on a day or week on the board, and hugo plans when it gets done.
      </p>
    )
  }
  return (
    <p
      id="item-plan-text"
      className="flex items-start gap-1.5 text-sm text-muted-foreground"
      data-testid="plan-text"
      data-status={plan.status}
    >
      {plan.status === 'on_track' ? null : (
        <TriangleAlertIcon
          aria-hidden="true"
          className={cn('mt-0.5 size-4 shrink-0', attention.icon)}
        />
      )}
      <span>
        {planSentence(plan, {
          kind: item.kind,
          planningMinutes: settings.data?.planningMinutesPerDay ?? null,
          thisYear: year,
        })}
      </span>
    </p>
  )
}
